import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";

import { FITNESS_CHANGED } from "@/lib/fitnessChanges";
export { FITNESS_CHANGED, notifyFitnessChanged } from "@/lib/fitnessChanges";
// Bound read-only auth + data loading; retry never confirms or repeats a write.
const FITNESS_READ_TIMEOUT_MS = 20_000;

export type FitnessStatus = "loading" | "ready" | "error" | "signed-out";

/** Account-bound reads shared by the active Fitness routes. */
export function useFitnessResource<T>(loader: (userId: string, assertCurrent: () => void) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [status, setStatus] = useState<FitnessStatus>("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const owner = useRef<string | null | undefined>(undefined);
  const generation = useRef(0);
  const request = useRef(0);
  const mounted = useRef(false);
  const deadlines = useRef(new Set<ReturnType<typeof setTimeout>>());

  const refresh = useCallback(async () => {
    const ticket = ++request.current;
    let epoch = generation.current;
    let settled = false;
    const current = () => !settled && mounted.current && ticket === request.current && epoch === generation.current;
    const assertCurrent = () => { if (!current()) throw new Error("Fitness session changed"); };
    setIsRefreshing(true);
    const read = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      assertCurrent();
      if (error) throw error;
      const next = user?.id ?? null;
      if (owner.current !== next) {
        epoch = ++generation.current; owner.current = next;
        setUserId(next); setData(null); setCheckedAt(null);
      }
      if (!next) { setStatus("signed-out"); return; }
      const result = await loader(next, assertCurrent);
      assertCurrent(); setData(result); setStatus("ready"); setCheckedAt(Date.now());
    };
    let timer: ReturnType<typeof setTimeout>;
    const deadline = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => reject(new Error("Fitness read timed out")), FITNESS_READ_TIMEOUT_MS);
      deadlines.current.add(timer);
    });
    try {
      await Promise.race([read(), deadline]);
    } catch (error) {
      if (current()) { console.error("[Fitness] read failed", error); setStatus("error"); }
    } finally {
      if (current()) setIsRefreshing(false);
      settled = true; clearTimeout(timer!); deadlines.current.delete(timer!);
    }
  }, [loader]);

  useEffect(() => {
    mounted.current = true;
    const pendingDeadlines = deadlines.current;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (!mounted.current || owner.current === next) return;
      owner.current = next; generation.current++; request.current++;
      setUserId(next); setData(null); setCheckedAt(null); setIsRefreshing(false);
      setStatus(next ? "loading" : "signed-out");
      if (next) queueMicrotask(() => { if (mounted.current) void refresh(); });
    });
    void refresh();
    const changed = () => { if (mounted.current && owner.current) void refresh(); };
    window.addEventListener(FITNESS_CHANGED, changed);
    return () => { mounted.current = false; generation.current++; request.current++; pendingDeadlines.forEach(clearTimeout); pendingDeadlines.clear(); subscription.unsubscribe(); window.removeEventListener(FITNESS_CHANGED, changed); };
  }, [refresh]);

  useAgendaAutoRefresh({ checkedAt, nextEvent: null, loading: isRefreshing, error: status === "error", enabled: !!userId || status === "error", refresh });
  const capture = useCallback(() => {
    const epoch = generation.current;
    return () => mounted.current && owner.current === userId && !!userId && generation.current === epoch;
  }, [userId]);
  return { data, userId, status, isRefreshing, checkedAt, refresh, capture };
}
